import { z } from 'zod';

/**
 * Utility function that handles comma delimited lists being transformed into arrays of the outputType provided.
 */
export const commaDelimitedList = <T = string>(
  outputType?: z.ZodArray<z.ZodType<T>>,
) => {
  const output = z
    .string()
    .nullable()
    .transform((v) => v?.split(',') ?? [])
    .pipe(outputType ?? z.string().array());

  return output as z.ZodPipeline<
    z.ZodEffects<z.ZodNullable<z.ZodString>, string[], string | null>,
    z.ZodArray<z.ZodType<T>, 'many'>
  >;
};

/**
 * Primary Key type for all tables, we use NanoIds with default settings
 */
export const PrimaryKeySchema = z
  .string()
  .nanoid()
  .describe(
    'A unique nanoid for the resource (for more information please see: https://zelark.github.io/nano-id-cc/)',
  );

/**
 * Adds a prefix to each key in the object
 */
export const withPrefix = <T extends z.AnyZodObject>(
  prefix: string,
  schema: T,
) => {
  return schema.transform((data) => {
    return Object.fromEntries(
      Object.entries(data).map(([key, value]) => [prefix + key, value]),
    );
  });
};

const JSONSchemaTypeSchema = z.enum([
  'null',
  'boolean',
  'object',
  'array',
  'number',
  'string',
  'integer',
]);

const JSONSchemaBaseSchema = z.object({
  $schema: z.string().url().optional(),
  $id: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  type: z
    .union([JSONSchemaTypeSchema, z.array(JSONSchemaTypeSchema)])
    .optional(),
  enum: z.array(z.any()).optional(),
  const: z.any().optional(),
  default: z.any().optional(),
});

export type JSONSchemaType = z.infer<typeof JSONSchemaBaseSchema> & {
  properties?: Record<string, JSONSchemaType>;
  additionalProperties?: boolean | JSONSchemaType;
  items?: JSONSchemaType | JSONSchemaType[];
  additionalItems?: boolean | JSONSchemaType;
  allOf?: JSONSchemaType[];
  anyOf?: JSONSchemaType[];
  oneOf?: JSONSchemaType[];
  not?: JSONSchemaType;
  if?: JSONSchemaType;
  then?: JSONSchemaType;
  else?: JSONSchemaType;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  format?: string;
  minimum?: number;
  maximum?: number;
  exclusiveMinimum?: number | boolean;
  exclusiveMaximum?: number | boolean;
  multipleOf?: number;
  minItems?: number;
  maxItems?: number;
  uniqueItems?: boolean;
  minProperties?: number;
  maxProperties?: number;
  required?: string[];
  definitions?: Record<string, JSONSchemaType>;
  $ref?: string;
};

const JSONSchemaPropertySchema: z.ZodType<JSONSchemaType> = z.lazy(() =>
  JSONSchemaBaseSchema.extend({
    // Object properties
    properties: z.record(JSONSchemaPropertySchema).optional(),
    additionalProperties: z
      .union([z.boolean(), JSONSchemaPropertySchema])
      .optional(),
    required: z.array(z.string()).optional(),

    // Array properties
    items: z
      .union([JSONSchemaPropertySchema, z.array(JSONSchemaPropertySchema)])
      .optional(),
    additionalItems: z
      .union([z.boolean(), JSONSchemaPropertySchema])
      .optional(),

    // String properties
    minLength: z.number().int().min(0).optional(),
    maxLength: z.number().int().min(0).optional(),
    pattern: z.string().optional(),
    format: z.string().optional(),

    // Number properties
    minimum: z.number().optional(),
    maximum: z.number().optional(),
    exclusiveMinimum: z.union([z.number(), z.boolean()]).optional(),
    exclusiveMaximum: z.union([z.number(), z.boolean()]).optional(),
    multipleOf: z.number().positive().optional(),

    // Array constraints
    minItems: z.number().int().min(0).optional(),
    maxItems: z.number().int().min(0).optional(),
    uniqueItems: z.boolean().optional(),

    // Object constraints
    minProperties: z.number().int().min(0).optional(),
    maxProperties: z.number().int().min(0).optional(),

    // Composition
    allOf: z.array(JSONSchemaPropertySchema).optional(),
    anyOf: z.array(JSONSchemaPropertySchema).optional(),
    oneOf: z.array(JSONSchemaPropertySchema).optional(),
    not: JSONSchemaPropertySchema.optional(),

    // Conditional
    if: JSONSchemaPropertySchema.optional(),
    then: JSONSchemaPropertySchema.optional(),
    else: JSONSchemaPropertySchema.optional(),

    // JSON Schema references
    definitions: z.record(JSONSchemaPropertySchema).optional(),
    $ref: z.string().optional(),
  }),
);

export const JSONSchema = z
  .unknown()
  .pipe(JSONSchemaPropertySchema)
  .describe('A valid JSON Schema object following Draft 7 specification');

/**
 * Converts snake_case to camelCase
 */
const toCamelCase = (str: string): string => {
  return str.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Converts camelCase to snake_case
 */
const toSnakeCase = (str: string): string => {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
};

/**
 * Transforms an object's keys from snake_case to camelCase recursively
 */
const transformKeysToCamelCase = (obj: unknown): unknown => {
  if (obj === null || obj === undefined) return obj;

  if (Array.isArray(obj)) {
    return obj.map(transformKeysToCamelCase);
  }

  if (typeof obj === 'object' && obj.constructor === Object) {
    return Object.fromEntries(
      Object.entries(obj as Record<string, unknown>).map(([key, value]) => [
        toCamelCase(key),
        transformKeysToCamelCase(value),
      ]),
    );
  }

  return obj;
};

/**
 * Creates a Zod schema that accepts both snake_case and camelCase inputs
 * but always outputs camelCase. Works recursively with nested objects.
 */
export const withCamelCaseTransform = <T extends z.ZodTypeAny>(
  schema: T,
): z.ZodEffects<T, z.infer<T>, unknown> => {
  return schema.transform((data) => transformKeysToCamelCase(data));
};

/**
 * Creates a union schema that accepts both snake_case and camelCase versions
 * of an object schema, but always outputs camelCase.
 */
export const withSnakeCaseSupport = <T extends z.ZodObject<z.ZodRawShape>>(
  schema: T,
): z.ZodUnion<
  [T, z.ZodEffects<z.ZodObject<z.ZodRawShape>, z.infer<T>, unknown>]
> => {
  // Create snake_case version of the schema
  const snakeCaseShape: z.ZodRawShape = Object.fromEntries(
    Object.entries(schema.shape).map(([key, value]) => [
      toSnakeCase(key),
      value,
    ]),
  );

  const snakeCaseSchema = z
    .object(snakeCaseShape)
    .transform((data) => transformKeysToCamelCase(data));

  return z.union([
    schema,
    snakeCaseSchema as z.ZodEffects<
      z.ZodObject<z.ZodRawShape>,
      z.infer<T>,
      unknown
    >,
  ]);
};
